import Data from 'data'
import Locale from 'data/locale'
import BinaryReader from './reader'

import { Quality } from 'common'
import { itemGetTypes } from 'logic/item'
import { itemTypeTree, presetItems } from 'logic/itemTypes'
import {
  parseModStats,
  parseRunewordStats,
  parseUniqueStats
} from 'logic/parser'

let statMap = null

export const gameQualityMap = [
  0,
  Quality.LOW,
  Quality.NORMAL,
  Quality.SUPERIOR,
  Quality.MAGIC,
  Quality.SET,
  Quality.RARE,
  Quality.UNIQUE,
  Quality.CRAFTED
]
const bodyMap = [
  null,
  'head',
  'neck',
  'tors',
  'rarm',
  'larm',
  'rrin',
  'lrin',
  'belt',
  'feet',
  'glov',
  'rarm2',
  'larm2'
]
const mkid = (id) => id.toString().padStart(3, '0')

const followStats = {
  item_maxdamage_percent: 1,
  firemindam: 1,
  lightmindam: 1,
  magicmindam: 1,
  coldmindam: 2,
  poisonmindam: 2
}

function addStat(stats, stat, param, value) {
  let param2
  switch (stat) {
    case 'item_skillonattack':
    case 'item_skillonkill':
    case 'item_skillondeath':
    case 'item_skillonhit':
    case 'item_skillonlevelup':
    case 'item_skillongethit':
      param2 = value
      value = param & 63
      param = param >> 6
      stat = `${stat}#${param}#${param2}`
      break
    case 'item_charged_skill':
      value = value >> 8
      param2 = param & 63
      param = param >> 6
      stat = `${stat}#${param}#${param2}`
      break
    case 'item_addclassskills':
    case 'item_nonclassskill':
    case 'state':
    case 'item_singleskill':
    case 'item_elemskill':
    case 'item_aura':
    case 'item_reanimate':
      stat = `${stat}#${param}`
      break
    case 'item_addskill_tab':
      param = (param >> 3) * 3 + (param & 7)
      stat = `${stat}#${param}`
      break
    // no default
  }
  stats[stat] = (stats[stat] || 0) + value
}

function parseParamStats(stats, statsSrc) {
  function escape(str) {
    return str.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  }
  for (const idFull in statsSrc) {
    let [id, param, param2] = idFull.split('#')
    const stat = Data.itemStatCost[id]
    if (!stat || !stat.descfunc) continue
    if (param2 === 'x' && (stat.descfunc === 13 || stat.descfunc === 27)) {
      const value = parseInt(param)
      const re = new RegExp(`^${escape(id)}#(\\d+)$`)
      for (const oid in stats) {
        const m = oid.match(re)
        if (m && stats[oid] === value) {
          delete stats[oid]
          stats[idFull] = parseInt(m[1])
          break
        }
      }
    }
  }
  return stats
}

function parseCharacter(data) {
  if (!statMap) {
    statMap = new Map()
    for (const [id, row] of Object.entries(Data.itemStatCost)) {
      statMap.set(row.id, id)
    }
  }

  const reader = new BinaryReader(data)
  if (reader.read32() !== 0xaa55aa55) throw Error('invalid header')
  const version = reader.read32()
  if (version !== 96 && version !== 97 && version !== 98 && version !== 99)
    throw Error('unsupported version')
  const D2R = version >= 97
  reader.skip(8)

  const result = {}
  result.weaponSet = reader.read32()
  result.name = reader.string(16)

  reader.seek(40)
  const classId = reader.read8()
  result.class = Data.classes[classId]

  reader.seek(179)
  const mercId = reader.read32()
  if (mercId) {
    result.mercName = reader.read16()
    result.merc = reader.read16().toString()
    const mercXP = reader.read32()
    const mercData = Data.hireling[result.merc]?.[0]
    if (mercData) {
      let L = mercData.level
      const M = mercData.explvl
      while (L < 98 && M * (L + 1) * (L + 1) * (L + 2) <= mercXP) {
        L += 1
      }
      result.mercLevel = L
    }
  }

  const questTable = () => ({
    denofevil: (reader.byte(2) & 1) !== 0,
    radamentslair: (reader.byte(18) & 1) !== 0,
    thegoldenbird: (reader.byte(40) & 1) !== 0,
    lamessenstome: (reader.byte(34) & 1) !== 0,
    thefallenangel: (reader.byte(50) & 1) !== 0,
    prisonofice: (reader.byte(74) & 1) !== 0
  })
  result.quests = []
  reader.seek(345)
  result.quests.push(questTable())
  reader.seek(441)
  result.quests.push(questTable())
  reader.seek(537)
  result.quests.push(questTable())

  reader.seek(765)
  if (reader.read16() !== 0x6667) throw Error('invalid stats header')
  const stats = {}
  let id = reader.bits(9)
  while (id !== 511) {
    const stat = statMap.get(id)
    if (!stat) throw Error(`unknown stat code ${id}`)
    stats[stat] = reader.bits(Data.itemStatCost[stat].csvbits)
    id = reader.bits(9)
  }
  reader.align()
  result.stats = {}
  const charStats = Data.charStats[result.class]
  result.stats.str = stats.strength - charStats.str
  result.stats.dex = stats.dexterity - charStats.dex
  result.stats.int = stats.energy - charStats.int
  result.stats.vit = stats.vitality - charStats.vit
  result.level = stats.level

  if (reader.read16() !== 0x6669) throw Error('invalid skills header')
  result.skills = {}
  const skillBase = classId < 5 ? classId * 30 + 6 : (classId - 5) * 30 + 221
  for (let i = 0; i < 30; ++i) {
    const level = reader.read8()
    if (level) result.skills[i + skillBase] = level
  }

  let itemId = 1
  const items = {}

  function parseItem(id, handler) {
    if (!D2R) {
      if (reader.read16() !== 0x4d4a) throw Error('invalid item header')
    }
    const item = {}
    reader.skipbits(4)
    item.unidentified = !reader.bits(1)
    reader.skipbits(6)
    const socketed = reader.bits(1)
    reader.skipbits(4)
    const ear = reader.bits(1)
    reader.skipbits(4)
    const simple = reader.bits(1)
    item.ethereal = reader.bits(1) !== 0
    reader.skipbits(1)
    const personalized = reader.bits(1)
    reader.skipbits(1)
    const runeword = reader.bits(1)
    reader.skipbits(D2R ? 8 : 15)
    const location = reader.bits(3)
    const bodyloc = reader.bits(4)
    const invcol = reader.bits(4)
    const invrow = reader.bits(3)
    reader.skipbits(1)
    const storage = reader.bits(3)
    let baseId
    if (D2R) {
      const basea = reader.char()
      const baseb = reader.char()
      const basec = reader.char()
      const based = reader.char()
      if (based !== ' ') throw Error(`invalid item code`)
      baseId = basea + baseb + basec
    } else {
      const basea = reader.bits(8)
      const baseb = reader.bits(8)
      const basec = reader.bits(8)
      const based = reader.bits(8)
      if (based !== 32) throw Error(`invalid item code`)
      baseId = String.fromCharCode(basea, baseb, basec)
    }
    if (!Data.items[baseId]) throw Error(`invalid item code ${baseId}`)
    item.base = baseId

    if (ear) {
      reader.skipbits(10)
      while (reader.bits(7)) {
        // do nothing
      }
      reader.align()
      return false
    }

    const base = Data.items[baseId]
    let socketedItems = 0

    if (!simple) {
      socketedItems = reader.bits(3)

      const types = itemGetTypes(baseId)

      reader.skipbits(32)
      item.ilvl = reader.bits(7)
      const quality = gameQualityMap[reader.bits(4)]
      item.quality = runeword ? Quality.RUNEWORD : quality
      if (reader.bits(1)) {
        item.iconIndex = reader.bits(3)
      }

      const mods = []
      if (reader.bits(1)) {
        mods.push(`am${mkid(reader.bits(11))}`)
      }

      let modid
      switch (quality) {
        case Quality.LOW:
          reader.skipbits(3)
          break
        case Quality.NORMAL:
          // 12 bits here sometimes?
          break
        case Quality.SUPERIOR:
          mods.push(`qm${mkid(reader.bits(3))}`)
          mods.pop() // too many mismatches
          break
        case Quality.MAGIC:
          if ((modid = reader.bits(11))) mods.push(`mp${mkid(modid)}`)
          if ((modid = reader.bits(11))) mods.push(`ms${mkid(modid)}`)
          break
        case Quality.SET:
          item.unique = `set${mkid(reader.bits(12))}`
          break
        case Quality.UNIQUE:
          item.unique = `unique${mkid(reader.bits(12))}`
          break
        case Quality.RARE:
        case Quality.CRAFTED:
          {
            const name1 = Data.rarePrefix[reader.bits(8) - 156]?.name
            const name2 = Data.rareSuffix[reader.bits(8) - 1]?.name
            item.name = `${Locale.strings[name1] || name1} ${
              Locale.strings[name2] || name2
            }`

            if (reader.bits(1)) mods.push(`mp${mkid(reader.bits(11))}`)
            if (reader.bits(1)) mods.push(`ms${mkid(reader.bits(11))}`)
            if (reader.bits(1)) mods.push(`mp${mkid(reader.bits(11))}`)
            if (reader.bits(1)) mods.push(`ms${mkid(reader.bits(11))}`)
            if (reader.bits(1)) mods.push(`mp${mkid(reader.bits(11))}`)
            if (reader.bits(1)) mods.push(`ms${mkid(reader.bits(11))}`)
          }
          break
        // no default
      }

      if (runeword) {
        let rwid = reader.bits(12)
        if (rwid === 2718) rwid = 48
        item.unique = `runeword${mkid(rwid)}`
        reader.skipbits(4)
      }

      if (personalized) {
        const bytes = []
        let chr
        while ((chr = reader.bits(7))) {
          bytes.push(chr)
        }
        item.personalized = String.fromCharCode(...bytes)
      }

      if (types.has('book')) {
        reader.skipbits(5)
      }

      if (reader.bits(1)) {
        reader.skipbits(96)
      }

      if (types.has('armo')) {
        let minac = reader.bits(11) - 10
        let maxac = minac

        if (item.ethereal) {
          minac = Math.ceil(minac / 1.5)
          maxac = Math.ceil((maxac + 1) / 1.5) - 1
        }
        if (minac <= base.maxac + 1 && maxac >= base.minac) {
          item.defense =
            (Math.max(minac, base.minac) + Math.min(maxac, base.maxac + 1)) >> 1
        } else {
          const minac2 = Math.ceil(minac / 1.5)
          const maxac2 = Math.ceil((maxac + 1) / 1.5) - 1
          if (minac <= base.maxac && maxac >= base.minac) {
            item.defense =
              (Math.max(minac2, base.minac) + Math.min(maxac2, base.maxac)) >> 1
            item.ethbugged = true
          } else {
            // i guess we just leave it
            item.defense = (minac + maxac) >> 1
          }
        }
      }

      if (types.has('weap') || types.has('armo')) {
        // durability
        if (reader.bits(8)) reader.skipbits(9)
      }
      if (base.stackable) reader.skipbits(9)
      if (socketed) item.sockets = reader.bits(4)

      const setflags = quality === Quality.SET ? reader.bits(5) : 0

      const stats = {}
      function parseStats(ignore) {
        let id = reader.bits(9)
        while (id !== 511) {
          const stat = statMap.get(id)
          if (!stat) throw Error(`unknown stat code ${id}`)
          const info = Data.itemStatCost[stat]
          const param = info.saveparambits && reader.bits(info.saveparambits)
          const value = reader.bits(info.savebits) - (info.saveadd || 0)
          addStat(stats, stat, param, value)

          let follow = followStats[stat] || 0
          while (follow--) {
            const stat = statMap.get(++id)
            const info = Data.itemStatCost[stat]
            const param = info.saveparambits && reader.bits(info.saveparambits)
            const value = reader.bits(info.savebits) - (info.saveadd || 0)
            addStat(stats, stat, param, value)
          }
          id = reader.bits(9)
        }
      }
      parseStats()
      if (setflags & 1) parseStats(true)
      if (setflags & 2) parseStats(true)
      if (setflags & 4) parseStats(true)
      if (setflags & 8) parseStats(true)
      if (setflags & 16) parseStats(true)
      if (runeword) parseStats()

      if (
        stats.mindamage ||
        stats.secondary_mindamage ||
        stats.item_throw_mindamage
      ) {
        stats.mindamage = Math.max(
          ...[
            stats.mindamage,
            stats.secondary_mindamage,
            stats.item_throw_mindamage
          ].filter(Boolean)
        )
        delete stats.secondary_mindamage
        delete stats.item_throw_mindamage
      }
      if (
        stats.maxdamage ||
        stats.secondary_maxdamage ||
        stats.item_throw_maxdamage
      ) {
        stats.maxdamage = Math.max(
          ...[
            stats.maxdamage,
            stats.secondary_maxdamage,
            stats.item_throw_maxdamage
          ].filter(Boolean)
        )
        delete stats.secondary_maxdamage
        delete stats.item_throw_maxdamage
      }

      item.stats = stats
      if (item.quality === Quality.SET || item.quality === Quality.UNIQUE) {
        item.uniqueValues = parseUniqueStats(item, (src) =>
          parseParamStats(stats, src)
        )
        if (!item.uniqueValues) {
          item.custom = true
        }
      } else if (item.quality === Quality.RUNEWORD) {
        const parsed = parseRunewordStats(
          item,
          null,
          (src) => parseParamStats(stats, src),
          mods
        )
        if (parsed) {
          Object.assign(item, parsed)
        } else {
          item.custom = true
        }
      } else {
        const parsed = parseModStats(
          item,
          null,
          null,
          null,
          (src) => parseParamStats(stats, src),
          mods
        )
        if (parsed) {
          Object.assign(item, parsed)
        } else {
          item.custom = true
        }
      }
      if (types.has('blun')) {
        stats.item_undeaddamage_percent =
          (stats.item_undeaddamage_percent || 0) + 50
      }
    } else {
      reader.skipbits(1)
    }
    reader.align()

    if (item.sockets || socketedItems) item.socketedItems = []

    for (let i = 0; i < socketedItems; ++i) {
      parseItem(itemId++, (id) => item.socketedItems.push(id))
    }

    if (
      item.unique &&
      !(
        Data.uniqueItems[item.unique] ||
        Data.setItems[item.unique] ||
        Data.runes[item.unique]
      )
    ) {
      return
    }

    if (presetItems[item.base]) {
      id = item.base
    } else if (itemTypeTree.filter(base.type)) {
      items[id] = item
    } else {
      return
    }
    switch (location) {
      case 0:
        if (storage === 1) handler(id, 'inventory', invrow * 10 + invcol)
        if (storage === 4) handler(id, 'cube', invrow * 3 + invcol)
        break
      case 1:
        if (bodyMap[bodyloc]) handler(id, 'body', bodyMap[bodyloc])
        break
      case 6:
        handler(id)
        break
      // no default
    }
  }

  function parseItemList(handler) {
    if (reader.read16() !== 0x4d4a) throw Error('invalid item table header')
    const count = reader.read16()
    for (let i = 0; i < count; ++i) {
      parseItem(itemId++, handler)
    }
  }

  result.items = {}
  result.inventory = []
  result.cube = []
  parseItemList((id, location, slot) => {
    if (location === 'body') result.items[slot] = id
    if (location === 'inventory') result.inventory[slot] = id
    if (location === 'cube') result.cube[slot] = id
  }) // character items

  if (reader.read16() !== 0x4d4a) throw Error('invalid item table header')
  const corpses = reader.read16()
  reader.skip(corpses * 12)
  for (let i = 0; i < corpses; ++i) {
    parseItemList(() => null) // corpses
  }

  if (!reader.eof()) {
    if (reader.read16() !== 0x666a) throw Error('invalid hireling header')
    result.mercItems = {}
    if (mercId) {
      parseItemList((id, location, slot) => {
        if (location === 'body') result.mercItems[slot] = id
      })
    }
  }

  if (!reader.eof()) {
    if (reader.read16() !== 0x666b) throw Error('invalid iron golem header')
    if (reader.read8()) {
      parseItem(itemId++, (id) => (result.ironGolem = id))
    }
  }

  if (!reader.eof()) throw Error('file too long')

  return { profile: result, items }
}

const readFile = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () => reject(reader.error)
    reader.onabort = () => reject()
    reader.readAsArrayBuffer(file)
  })

export async function parseFile(file) {
  const data = await readFile(file)
  return parseCharacter(new Uint8Array(data))
}
